import { useEffect, useRef, type ReactNode } from 'react';
import { Maximize2, Minimize2, X } from '../../icons';
import './DocPanel.css';

/**
 * ④ 우측 상세(문서) 표준 껍데기 — 설계 §5-④.
 * docs/design/design-20260717-common-layout-skeleton.md
 *
 * 헤더(타이틀 + 확장 ⤢ + 닫기 ») → 본문 slot(스크롤). overlay/expanded 모드에서는 Esc 로 닫힌다
 * (설계 §5-공통 접근성). 실제 상태(closed/inline/overlay/expanded)는 `layoutShellStore` 가 소유하고
 * LayoutShell 이 표시를 결정하므로, 본 컴포넌트는 표시된 뒤의 껍데기·컨트롤만 담당한다.
 */
export interface DocPanelProps {
  title: ReactNode;
  /** 확장(⤢) 핸들러 (선택). */
  onExpand?: () => void;
  /**
   * 현재 전체화면인가 (선택, 기본 false).
   *
   * 토글 버튼인데 아이콘·설명이 고정이면 "눌렀는데 반응이 없다" 로 읽힌다. 넘겨 주면
   * ⤢(펼치기) ↔ ⤡(되돌리기) 로 바뀐다. 안 넘기면 종전과 동일(항상 ⤢).
   */
  expanded?: boolean;
  /** 닫기(») 핸들러. Esc 로도 호출. */
  onClose: () => void;
  /** Esc 로 닫기 활성화 여부 (overlay/expanded 에서 true 권장). 기본 true. */
  closeOnEsc?: boolean;
  /**
   * 화면별 액션 (선택) — 헤더의 확장·닫기 **왼쪽**에 붙는다 (2026-08-14 additive).
   *
   * 이걸 두기 전에는 소비자가 본문 첫 줄에 자기 툴바를 그렸다. 그러면 헤더 한 줄 + 툴바 한 줄로
   * **두 줄**이 되어 전체화면에서 특히 세로가 아깝고, 스크롤을 내리면 툴바가 본문과 함께
   * 위로 사라진다(저장 버튼이 화면 밖으로 나간다). 헤더는 스크롤 밖이라 여기 붙으면 항상 보인다.
   * 안 넘기면 종전과 동일 — 기존 호출부는 한 줄도 바뀌지 않는다.
   */
  actions?: ReactNode;
  children: ReactNode;
}

export function DocPanel({
  title, onExpand, expanded = false, onClose, closeOnEsc = true, actions, children,
}: DocPanelProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!closeOnEsc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeOnEsc, onClose]);

  return (
    <div className="doc-panel" ref={rootRef} role="complementary" aria-label="문서 패널">
      <div className="doc-panel__header">
        <div className="doc-panel__title">{title}</div>
        <div className="doc-panel__actions">
          {actions && <div className="doc-panel__extra">{actions}</div>}
          {onExpand && (
            <button
              type="button"
              className="doc-panel__btn"
              onClick={onExpand}
              aria-label={expanded ? '전체화면 해제' : '전체 확장'}
              aria-pressed={expanded}
              title={expanded ? '전체화면 해제' : '전체화면'}
            >
              {expanded
                ? <Minimize2 size={15} aria-hidden />
                : <Maximize2 size={15} aria-hidden />}
            </button>
          )}
          <button type="button" className="doc-panel__btn" onClick={onClose} aria-label="패널 닫기">
            <X size={16} aria-hidden />
          </button>
        </div>
      </div>
      <div className="doc-panel__body scrollbar-thin">{children}</div>
    </div>
  );
}
